import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, ZoomIn, ZoomOut, RotateCw, X, Check } from 'lucide-react';

interface AvatarCropperProps {
  currentAvatarUrl: string | null;
  onCropComplete: (blob: Blob) => void;
  onCancel: () => void;
}

const CANVAS_SIZE = 280;
const OUTPUT_SIZE = 400;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

export const AvatarCropper: React.FC<AvatarCropperProps> = ({
  currentAvatarUrl,
  onCropComplete,
  onCancel,
}) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    ctx.fillStyle = '#1a1030';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    ctx.save();
    ctx.translate(CANVAS_SIZE / 2, CANVAS_SIZE / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    const aspect = image.width / image.height;
    let drawW, drawH;
    if (aspect > 1) {
      drawH = CANVAS_SIZE;
      drawW = drawH * aspect;
    } else {
      drawW = CANVAS_SIZE;
      drawH = drawW / aspect;
    }

    ctx.drawImage(
      image,
      -drawW / 2 + offset.x,
      -drawH / 2 + offset.y,
      drawW,
      drawH
    );
    ctx.restore();

    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2 - 1, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }, [image, zoom, rotation, offset]);

  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setZoom(1);
        setRotation(0);
        setOffset({ x: 0, y: 0 });
      };
      img.src = ev.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!image) return;
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!image || e.touches.length !== 1) return;
    const touch = e.touches[0];
    setDragging(true);
    setDragStart({ x: touch.clientX - offset.x, y: touch.clientY - offset.y });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dragging || e.touches.length !== 1) return;
    e.preventDefault();
    const touch = e.touches[0];
    setOffset({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  const handleTouchEnd = () => {
    setDragging(false);
  };

  const handleCrop = async () => {
    if (!image) return;
    setSaving(true);

    try {
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = OUTPUT_SIZE;
      outputCanvas.height = OUTPUT_SIZE;
      const ctx = outputCanvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

      const scale = OUTPUT_SIZE / CANVAS_SIZE;

      ctx.save();
      ctx.translate(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(zoom * scale, zoom * scale);

      const aspect = image.width / image.height;
      let drawW, drawH;
      if (aspect > 1) {
        drawH = CANVAS_SIZE;
        drawW = drawH * aspect;
      } else {
        drawW = CANVAS_SIZE;
        drawH = drawW / aspect;
      }

      ctx.drawImage(
        image,
        -drawW / 2 + offset.x,
        -drawH / 2 + offset.y,
        drawW,
        drawH
      );
      ctx.restore();

      ctx.save();
      ctx.globalCompositeOperation = 'destination-in';
      ctx.beginPath();
      ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      outputCanvas.toBlob(
        (blob) => {
          if (blob) {
            onCropComplete(blob);
          }
          setSaving(false);
        },
        'image/png',
        1
      );
    } catch {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col items-center">
      {!image ? (
        <div className="flex flex-col items-center">
          {currentAvatarUrl && (
            <img
              src={currentAvatarUrl}
              alt="Current avatar"
              className="w-24 h-24 rounded-full object-cover border-4 border-white/10 mb-4"
            />
          )}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-5 py-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium"
          >
            <Upload size={18} />
            Choose Photo
          </button>
          <p className="text-white/40 text-xs mt-2">JPG, PNG, or WebP</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      ) : (
        <>
          <div className="relative mb-4">
            <div
              className="rounded-full overflow-hidden cursor-grab active:cursor-grabbing"
              style={{ width: CANVAS_SIZE, height: CANVAS_SIZE }}
            >
              <canvas
                ref={canvasRef}
                width={CANVAS_SIZE}
                height={CANVAS_SIZE}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className="block"
              />
            </div>
            <p className="text-center text-white/40 text-xs mt-2">
              Drag to reposition
            </p>
          </div>

          <div className="flex items-center gap-4 mb-4 w-full max-w-[280px]">
            <button
              type="button"
              onClick={() => setZoom(Math.max(MIN_ZOOM, zoom - 0.1))}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ZoomOut size={16} />
            </button>
            <input
              type="range"
              min={MIN_ZOOM * 100}
              max={MAX_ZOOM * 100}
              value={zoom * 100}
              onChange={(e) => setZoom(Number(e.target.value) / 100)}
              className="flex-1 accent-sky-500 h-1"
            />
            <button
              type="button"
              onClick={() => setZoom(Math.min(MAX_ZOOM, zoom + 0.1))}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ZoomIn size={16} />
            </button>
            <button
              type="button"
              onClick={() => setRotation((rotation + 90) % 360)}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            >
              <RotateCw size={16} />
            </button>
          </div>

          <div className="flex items-center gap-3 w-full max-w-[280px]">
            <button
              type="button"
              onClick={() => {
                setImage(null);
                setOffset({ x: 0, y: 0 });
                setZoom(1);
                setRotation(0);
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-sm font-medium"
            >
              <X size={16} />
              Change Photo
            </button>
            <button
              type="button"
              onClick={handleCrop}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Check size={16} />
              )}
              {saving ? 'Saving...' : 'Apply'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
